import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet";
import L from "leaflet";
import markerIconUrl from "leaflet/dist/images/marker-icon.png";
import markerShadowUrl from "leaflet/dist/images/marker-shadow.png";

const DEFAULT_CENTER = {
  lat: 39.9334,
  lng: 32.8597,
};

const markerIcon = new L.Icon({
  iconUrl: markerIconUrl,
  shadowUrl: markerShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

function MapClickHandler({ onSelect }) {
  useMapEvents({
    click(event) {
      onSelect(event.latlng);
    },
  });

  return null;
}

function LocationPicker({ initialLocation, onConfirm, onCancel }) {
  const [selectedPosition, setSelectedPosition] = useState(
    initialLocation
      ? { lat: initialLocation.lat, lng: initialLocation.lng }
      : null
  );
  const [selectedAddress, setSelectedAddress] = useState(
    initialLocation?.address || ""
  );
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [userCenter, setUserCenter] = useState(null);

  const mapCenter = useMemo(() => {
    if (selectedPosition) {
      return selectedPosition;
    }

    if (userCenter) {
      return userCenter;
    }

    return DEFAULT_CENTER;
  }, [selectedPosition, userCenter]);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCenter({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        setGeoError(
          "Konumunuz alınamadı. Harita üzerinden manuel seçim yapabilirsiniz."
        );
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const reverseGeocode = async (lat, lng) => {
    setIsLoadingAddress(true);
    setGeoError("");

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );

      if (!response.ok) {
        throw new Error("Adres bilgisi alınamadı.");
      }

      const data = await response.json();
      setSelectedAddress(data.display_name || "Adres bilgisi bulunamadı.");
    } catch (error) {
      setSelectedAddress("");
      setGeoError("Adres bilgisi alınırken bir hata oluştu.");
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const handleSelect = ({ lat, lng }) => {
    setSelectedPosition({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const handleConfirm = () => {
    if (!selectedPosition) {
      setGeoError("Lütfen haritadan bir teslim alma konumu seçin.");
      return;
    }

    onConfirm({
      lat: selectedPosition.lat,
      lng: selectedPosition.lng,
      address: selectedAddress || "Adres bilgisi alınamadı.",
    });
  };

  return (
    <section className="location-picker card">
      <div className="section-head">
        <h3>Teslim Alma Konumu</h3>
        <p>Haritaya dokunarak pin bırakın veya pini sürükleyin.</p>
      </div>

      <div className="map-shell">
        <MapContainer
          center={mapCenter}
          zoom={selectedPosition ? 16 : userCenter ? 14 : 6}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onSelect={handleSelect} />
          {selectedPosition ? (
            <Marker
              position={selectedPosition}
              draggable
              icon={markerIcon}
              eventHandlers={{
                dragend: (event) => {
                  const latlng = event.target.getLatLng();
                  handleSelect(latlng);
                },
              }}
            />
          ) : null}
        </MapContainer>
      </div>

      <div className="location-meta">
        <span className="summary-label">Seçilen Adres</span>
        <p>
          {isLoadingAddress
            ? "Adres bilgisi alınıyor..."
            : selectedAddress || "Henüz bir konum seçilmedi."}
        </p>
        {geoError ? <div className="inline-alert">{geoError}</div> : null}
      </div>

      <div className="button-row">
        <button type="button" className="secondary-button" onClick={onCancel}>
          İptal
        </button>
        <button type="button" className="primary-button" onClick={handleConfirm}>
          Konumu Onayla
        </button>
      </div>
    </section>
  );
}

export default LocationPicker;
